import { useRef, useState, useEffect } from 'react';
import COS from 'cos-js-sdk-v5';
import { Base64 } from 'js-base64';
import { ENV } from '@home/_config/config';
import { COS_USE_ACCELERATE_ENVS } from '@intelligence/_config/config';
import { COS_BUCKET_NEW } from '@gp/constants/const';

// 默认超时时长 1小时，单位为秒
const COS_TOKEN_TIMEOUT = 1 * 60 * 60;

// 仅预发布、正式 支持COS全球域名加速请求
export const getUseAccelerateByENV = () => COS_USE_ACCELERATE_ENVS.includes(ENV);

/**
 * cos插件相关封装
 * @param options useCOS hook配置
 * @returns cos插件相关封装
 */
export default function useCOS(options: IUseCOSOptions) {
  const { useAccelerate = false, baseCOSConfigFetcher, authorizationFetcher,
  } = options || {};

  // COS插件基础配置是否初始化完成（从接口获取region、bucket）
  const [isCOSInited, setIsCOSInited] = useState(false);
  // COS插件实例
  const cos = useRef<COS>();
  // COS操作方法的基础参数
  const baseParams = useRef<ICOSOperationBaseParams>({
    region: '',
    bucket: '',
  });

  /**
   * 获取图片预览链接
   * @param params 获取图片预览链接的参数
   * @returns 图片预览链接
   */
  const getImagePreviewUrl = async (params: { key: string; }) => {
    if (!cos?.current) throw new Error('cos插件未初始化');
    const userName = sessionStorage.getItem('userName');
    if (!userName) throw new Error('can not preview document without username watermark');

    let { url } = await getObjectUrl(params);
    const watermarkConfig = `watermark/2/text/${Base64.encodeURI(
      userName,
    )}/fontsize/20/fill/${Base64.encodeURI(
      '#C0C0C0',
    )}/dissolve/60/dx/50/dy/100/batch/1/spacing/50/degree/315`;
    if (url?.includes('?')) {
      url = `${url}&${watermarkConfig}`;
    } else {
      url = `${url}?${watermarkConfig}`;
    }
    return { url };
  };

  /**
   * 获取文档预览链接
   * @param params 获取文档预览链接的参数
   * @returns 文档预览链接
   */
  const getDocumentPreviewUrl = (
    params: {
      key: string;
    },
    previewOptions?: {
      // 指定目标文件类型，支持的文件类型请见腾讯云COS文档
      srcType?: string;
      // 对象下载签名，如果预览的对象为私有读时，需要传入签名，注意：需要进行 urlencode
      sign?: string;
      // 是否可复制。默认为可复制，填入值为1；不可复制，填入值为0
      copyable?: '1' | '0';
      // 是否开启水印，默认开启
      useHtmlwatermark?: boolean;
      // 水印文字，需要经过 URL 安全 的 Base64 编码，默认为空
      htmlwaterword?: string;
      // 水印 RGBA（颜色和透明度），需要经过 URL 安全 的 Base64 编码，默认为：rgba(192,192,192,0.6)
      htmlfillstyle?: string;
      // 水印文字样式，需要经过 URL 安全 的 Base64 编码，默认为：bold 20px Serif
      htmlfront?: string;
      // 水印文字水平间距，单位 px，默认为50
      htmlhorizontal?: string;
      // 水印文字垂直间距，单位 px，默认为100
      htmlvertical?: string;
    },
  ): Promise<{ url: string; }> => {
    return new Promise((resolve, reject) => {
      if (!cos?.current) return reject('cos插件未初始化');
      const { region = '', bucket = '' } = baseParams?.current || {};
      const { key } = params || {};
      const {
        copyable = '0',
        htmlhorizontal,
        htmlvertical,
        htmlfillstyle,
        htmlfront,
        useHtmlwatermark = true,
        htmlwaterword,
      } = previewOptions || {};
      const userName = sessionStorage.getItem('userName');
      if (!userName)
        return reject(new Error('can not preview document without username watermark'));
      const htmlwaterwordNew = (() => {
        if (useHtmlwatermark === false) {
          return '';
        }

        if (!!htmlwaterword) {
          return Base64.encodeURI(htmlwaterword);
        }

        return Base64.encodeURI(userName);
      })();
      cos?.current?.getObjectUrl(
        {
          Region: region,
          Bucket: bucket,
          // 存储在桶里的对象键（例如1.jpg，a/b/test.txt），必须字段
          Key: key,
          Query: {
            // 必须，数据万象处理能力，文档预览固定为 doc-preview
            'ci-process': 'doc-preview',
            dstType: 'html',
            // 是否可复制。默认为可复制，填入值为1；不可复制，填入值为0
            copyable,
            // 水印文字，需要经过 URL 安全 的 Base64 编码，默认为空
            htmlwaterword: htmlwaterwordNew,
            htmlfillstyle: htmlfillstyle ? Base64.encodeURI(htmlfillstyle) : '',
            htmlfront: htmlfront ? Base64.encodeURI(htmlfront) : '',
            htmlhorizontal,
            htmlvertical,
          },
          // 签名几秒后失效，默认900秒，这里设置成1小时有效时间（文档预览）
          Expires: 1 * 60 * 60,
          // 获取带签名的对象URL，因为url权限是私有的，不带签名获取是拒绝访问的，带签名获取的话签名是有过期时间的
          Sign: true,
        },
        (err: any, data: any) => {
          if (err) {
            reject(err);
            return;
          }
          /* url为对象访问url */
          const url = data.Url;
          resolve({ url });
        },
      );
    });
  };

  /**
   * 获取对象访问链接
   * @param params 获取对象访问链接参数
   * @returns 对象访问链接
   */
  const getObjectUrl = async (params: {
    // 存储在桶里的对象键（例如1.jpg，a/b/test.txt），必须字段
    key: string;
    // 是否需要带签名，默认值为true
    sign?: boolean;
  }): Promise<{
    // 对象访问链接
    url: string;
    // 对象下载链接
    downloadUrl: string;
  }> => {
    return new Promise((resolve, reject) => {
      if (!cos?.current) return reject('cos插件未初始化');
      const { region = '', bucket = '' } = baseParams?.current || {};
      const { key, sign = true } = params || {};

      cos?.current?.getObjectUrl(
        {
          Region: region,
          Bucket: bucket,
          // 存储在桶里的对象键（例如1.jpg，a/b/test.txt），必须字段
          Key: key,
          // 签名几秒后失效，默认900秒，这里设置成5年有效时间
          Expires: 5 * 365 * 24 * 60 * 60,
          // 获取带签名的对象URL，因为url权限是私有的，不带签名获取是拒绝访问的，带签名获取的话签名是有过期时间的
          Sign: sign,
        },
        (err: any, data: any) => {
          if (err) {
            reject(err);
            return;
          }
          /* url为对象访问url */
          const url = data.Url;
          /* 复制downloadUrl的值到浏览器打开会自动触发下载 */
          const downloadUrl = `${
            url + (url.indexOf('?') > -1 ? '&' : '?')
          }response-content-disposition=attachment`; // 补充强制下载的参数
          resolve({ url, downloadUrl });
        },
      );
    });
  };

  /**
   * 下载对象
   * @param params 下载对象访问链接参数
   * @returns 对象内容
   */
  const getObject = async (params: {
    // 存储在桶里的对象键（例如1.jpg，a/b/test.txt），必须字段
    key: string;
    // 指定下载返回的数据格式，默认值为blob
    responseType?: 'blob' | 'text' | 'arraybuffer';
  }): Promise<any> => {
    return new Promise((resolve, reject) => {
      if (!cos?.current) return reject('cos插件未初始化');
      const { region = '', bucket = '' } = baseParams?.current || {};
      const { key, responseType = 'blob' } = params || {};

      cos?.current?.getObject(
        {
          Region: region,
          Bucket: bucket,
          // 存储在桶里的对象键（例如1.jpg，a/b/test.txt），必须字段
          Key: key,
          // 指定下载返回的数据格式
          DataType: responseType,
        },
        (err: any, data: any) => {
          if (err) {
            reject(err);
            return;
          }
          resolve(data?.Body);
        },
      );
    });
  };

  /**
   * 上传对象
   * @param params 上传对象参数
   * @returns 上传对象Promise
   */
  const putObject = async (params: {
    key: string;
    file: File;
    onProgress?: COS.onProgress;
  }): Promise<any> => {
    return new Promise((resolve, reject) => {
      const { region = '', bucket = '' } = baseParams?.current || {};
      const { key, file, onProgress } = params || {};
      if (!cos?.current) return reject('cos插件未初始化');
      cos?.current?.putObject(
        {
          Region: region,
          Bucket: bucket,
          // 存储在桶里的对象键（例如1.jpg，a/b/test.txt），必须字段
          Key: key,
          // BIDS广州存储桶存储类型不确定，不传，不然上传不了
          StorageClass: bucket === COS_BUCKET_NEW ? '' : 'STANDARD',
          Body: file, // 上传文件对象
          onProgress(progressData: any) {
            onProgress?.(progressData);
          },
        },
        (err: any, data: any) => {
          if (err) {
            reject(err);
          } else {
            resolve(data);
          }
        },
      );
    });
  };

  const headObject = (params: {
    key: string;
  }): Promise<{ exist: boolean; data: any; err: any; }> => {
    return new Promise((resolve, reject) => {
      const { region = '', bucket = '' } = baseParams?.current || {};
      const { key } = params || {};
      if (!cos?.current) return reject('cos插件未初始化');
      cos?.current?.headObject(
        {
          Region: region,
          Bucket: bucket,
          Key: key,
        },
        (err, data) => {
          resolve({ exist: !!data, data, err });
        },
      );
    });
  };

  const isObjectExist = async (params: { key: string; }): Promise<boolean> => {
    const { exist } = await headObject(params);
    return exist;
  };

  /**
   * 初始化COS操作方法的基础参数
   * @param payload COS配置信息
   */
  const initBaseParams = (payload: ICOSAuthorizationData) => {
    const { region = '', bucket = '' } = payload || {};
    baseParams.current.region = region;
    baseParams.current.bucket = bucket;
  };

  /**
   * 获取COS基础配置，region、bucket
   */
  useEffect(() => {
    (async () => {
      try {
        const { region = '', bucket = '' } = await baseCOSConfigFetcher();
        if (region && bucket) {
          baseParams.current.region = region;
          baseParams.current.bucket = bucket;
          setIsCOSInited(true);
        }
      } catch (exception) {
        console.warn('fetch cos base config exception: ', exception);
      }
    })();
  }, []);

  /**
   * 初始化COS插件，配置获取权限信息回调
   */
  useEffect(() => {
    cos.current = new COS({
      // 指定 true，使用全球加速域名请求
      UseAccelerate: useAccelerate,
      async getAuthorization(options, callback) {
        try {
          const authorizationData = await authorizationFetcher(options);
          const {
            tmp_secret_id = '',
            tmp_secret_key = '',
            session_token = '',
          } = authorizationData || {};
          const startTime = Math.floor(Date.now() / 1000);
          // cos token过期时间 比 预设超时时长少一分钟，防止出现前端未超时，COS服务端超时的情况
          const expiredTime = Math.floor(startTime + COS_TOKEN_TIMEOUT - 1 * 60);
          initBaseParams(authorizationData);
          callback({
            TmpSecretId: tmp_secret_id,
            TmpSecretKey: tmp_secret_key,
            SecurityToken: session_token,
            // cos token开始时间 时间戳，单位秒
            StartTime: startTime,
            // cos token过期时间 时间戳，单位秒
            ExpiredTime: expiredTime,
            // 细粒度控制权限需要设为 true，会限制密钥只在相同请求时重复使用
            ScopeLimit: false,
          });
        } catch (exception: any) {
          console.warn('init cos plugin exception: ', exception?.message);
        }
      },
    });
  }, []);

  return {
    cos,
    isCOSInited,
    getObjectUrl,
    getObject,
    getImagePreviewUrl,
    getDocumentPreviewUrl,
    putObject,
    headObject,
    isObjectExist,
  };
}
